import React from 'react';
import { SxProps } from '@mui/material/styles';
import OfficeIcon from '@mui/icons-material/Business';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ConstructionIcon from '@mui/icons-material/Construction';
import HomeIcon from '@mui/icons-material/Home';
import ApartmentIcon from '@mui/icons-material/Apartment';
import FactoryIcon from '@mui/icons-material/Factory';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StoreIcon from '@mui/icons-material/Store';
import GarageIcon from '@mui/icons-material/Garage';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ParkIcon from '@mui/icons-material/Park';
import CategoryIcon from '@mui/icons-material/Category';

const ICON_MAP: Record<string, React.ElementType> = {
  office: OfficeIcon,
  warehouse: WarehouseIcon,
  construction: ConstructionIcon,
  home: HomeIcon,
  apartment: ApartmentIcon,
  factory: FactoryIcon,
  location: LocationOnIcon,
  store: StoreIcon,
  garage: GarageIcon,
  shipping: LocalShippingIcon,
  park: ParkIcon,
  general: CategoryIcon,
};

export const DEFAULT_GEOFENCE_ICON = 'general';
export const DEFAULT_GEOFENCE_COLOR = '#3B82F6';

interface GeofenceCategoryIconProps {
  iconKey?: string | null;
  sx?: SxProps;
  className?: string;
}

export const GeofenceCategoryIcon: React.FC<GeofenceCategoryIconProps> = ({
  iconKey,
  sx,
  className,
}) => {
  const Icon = ICON_MAP[iconKey || DEFAULT_GEOFENCE_ICON] ?? CategoryIcon;
  return <Icon sx={sx} className={className} />;
};

export const isKnownGeofenceIcon = (key?: string | null): boolean =>
  !!key && key in ICON_MAP;

/**
 * Sprint 16, story-077. `@react-google-maps/api`'s `<Marker icon>` only accepts
 * `string | google.maps.Icon | google.maps.Symbol` (see `@types/google.maps`'s
 * `MarkerOptions.icon`) — it cannot render `GeofenceCategoryIcon`, a React/MUI component. This is
 * a second, marker-only icon representation, kept alongside (not replacing) the React component
 * above, which continues to serve every existing non-map call site unchanged.
 *
 * One 24x24-viewBox SVG path per `ICON_MAP` key, sourced from the same Material Symbols outline
 * set `@mui/icons-material` wraps (read directly from each icon's own compiled `d` attribute, so
 * the marker glyph and the `GeofenceCategoryIcon` glyph used elsewhere read as the same family).
 * `garage` and `general` compose more than one shape in their MUI source (circles + a path);
 * `google.maps.Symbol`'s `path` takes a single SVG path string, so their circles are expressed as
 * two-arc circle subpaths concatenated into one path string — a standard SVG technique, not an
 * approximation of the shape itself.
 */
export const GEOFENCE_MARKER_ICON_PATHS: Record<string, string> = {
  general:
    'm12 2-5.5 9h11z M22 17.5 A4.5 4.5 0 1 0 13 17.5 A4.5 4.5 0 1 0 22 17.5 Z M3 13.5h8v8H3z',
  office:
    'M12 7V3H2v18h20V7zM6 19H4v-2h2zm0-4H4v-2h2zm0-4H4V9h2zm0-4H4V5h2zm4 12H8v-2h2zm0-4H8v-2h2zm0-4H8V9h2zm0-4H8V5h2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8zm-2-8h-2v2h2zm0 4h-2v2h2z',
  warehouse:
    'M22 21V7L12 3 2 7v14h5v-9h10v9zm-11-2H9v2h2zm2-3h-2v2h2zm2 3h-2v2h2z',
  construction:
    'm13.7826 15.1719 2.1213-2.1213 5.9963 5.9962-2.1213 2.1213zM17.5 10c1.93 0 3.5-1.57 3.5-3.5 0-.58-.16-1.12-.41-1.6l-2.7 2.7-1.49-1.49 2.7-2.7c-.48-.25-1.02-.41-1.6-.41C15.57 3 14 4.57 14 6.5c0 .41.08.8.21 1.16l-1.85 1.85-1.78-1.78.71-.71-1.41-1.41L12 3.49c-1.17-1.17-3.07-1.17-4.24 0L4.22 7.03l1.41 1.41H2.81l-.71.71 3.54 3.54.71-.71V9.15l1.41 1.41.71-.71 1.78 1.78-7.41 7.41 2.12 2.12L16.34 9.79c.36.13.75.21 1.16.21',
  home: 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z',
  apartment:
    'M17 11V3H7v4H3v14h8v-4h2v4h8V11zM7 19H5v-2h2zm0-4H5v-2h2zm0-4H5V9h2zm4 4H9v-2h2zm0-4H9V9h2zm0-4H9V5h2zm4 8h-2v-2h2zm0-4h-2V9h2zm0-4h-2V5h2zm4 12h-2v-2h2zm0-4h-2v-2h2z',
  factory:
    'M22 10v12H2V10l7-3v2l5-2v3zm-4.8-1.5L18 2h3l.8 6.5zM11 18h2v-4h-2zm-4 0h2v-4H7zm10-4h-2v4h2z',
  location:
    'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7m0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5',
  store:
    'M20 4H4v2h16zm1 10v-2l-1-5H4l-1 5v2h1v6h10v-6h4v6h2v-6zm-9 4H6v-4h6z',
  garage:
    'M16 13 A1 1 0 1 0 14 13 A1 1 0 1 0 16 13 Z M10 13 A1 1 0 1 0 8 13 A1 1 0 1 0 10 13 Z m-1.67-5.5-.66 2h8.66l-.66-2z M20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2m-1 15.69c0 .45-.35.81-.78.81h-.44c-.44 0-.78-.36-.78-.81V16.5H7v1.19c0 .45-.35.81-.78.81h-.44c-.43 0-.78-.36-.78-.81v-6.5c.82-2.47 1.34-4.03 1.56-4.69.05-.16.12-.29.19-.4.02-.02.03-.04.05-.06.38-.53.92-.54.92-.54h8.56s.54.01.92.53c.02.03.03.05.05.07.07.11.14.24.19.4.22.66.74 2.23 1.56 4.69z',
  shipping:
    'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5m13.5-9 1.96 2.5H17V9.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5',
  park: 'M17 12h2L12 2 5.05 12H7l-3.9 6h6.92v4h3.96v-4H21z',
};

export const getGeofenceMarkerIcon = (
  iconKey: string | null | undefined,
  color: string,
): google.maps.Symbol => ({
  path:
    GEOFENCE_MARKER_ICON_PATHS[iconKey || DEFAULT_GEOFENCE_ICON] ??
    GEOFENCE_MARKER_ICON_PATHS[DEFAULT_GEOFENCE_ICON],
  fillColor: color,
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 1,
  scale: 1.1,
  anchor: new google.maps.Point(12, 22),
});
